import { ServiceError, fetchBalance, fundAddress } from './api.js';
import { showDeviceFact } from './banner.js';
import { detail, element, fill, make, short, xlm } from './dom.js';
import { PasskeyKitError, PasskeyKitErrorCode } from 'passkey-kit';
import { createAccount, restoreAccount, signInAccount } from './passkey.js';
import { clearAccount, getAccount, getChain } from './store.js';
import type { StoredAccount } from './store.js';

/**
 * The account screen.
 *
 * A person creates a passkey account, funds it, and sees what it holds. No
 * seed phrase exists: the passkey stays inside the device's authenticator, and
 * the account is a contract that accepts it as a signer.
 */

/** Read a message out of a caught value of unknown type. */
function reason(caught: unknown): string {
  if (caught instanceof ServiceError) return caught.message;
  if (caught instanceof Error) return caught.message;
  return 'the application could not finish that step';
}

/**
 * Why a sign in did not find an account, in words a person can act on.
 *
 * The kit's own messages describe a credential and a derivation. A person who
 * reinstalled the application needs to know whether to try another passkey,
 * create an account, or come back with a network.
 */
function signInReason(caught: unknown): string {
  if (caught instanceof PasskeyKitError) {
    if (caught.code === PasskeyKitErrorCode.WALLET_NOT_FOUND) {
      return (
        'That passkey holds no account on this name. Create an account, or ' +
        'try the passkey that you made here.'
      );
    }
    if (caught.code === PasskeyKitErrorCode.WALLET_OWNERSHIP_MISMATCH) {
      return 'That passkey is not a signer on the account that it points to.';
    }
    if (caught.code === PasskeyKitErrorCode.WEBAUTHN_CANCELLED) {
      return 'Your device did not confirm the passkey.';
    }
  }
  return reason(caught);
}

/** Show one failure, in the same shape that every screen uses. */
function showProblem(panel: HTMLElement, message: string): void {
  const box = make('div', 'result result-bad');
  box.append(make('h2', 'result-title', 'That did not work'), make('p', 'result-advice', message));
  fill(panel, box);
}

/** Wire the account screen to the document. */
export function startAccountScreen(): void {
  const panel = element('account-panel', HTMLDivElement);
  const create = element('account-create', HTMLButtonElement);
  const name = element('account-name', HTMLInputElement);
  const form = element('account-form', HTMLFormElement);
  const signIn = element('account-signin', HTMLButtonElement);
  const signInBox = element('account-signin-box', HTMLDivElement);

  /**
   * Whether this account belongs to the name that serves the page.
   *
   * A passkey binds to the domain that created it. An account created under
   * one name keeps its funds under another, and its passkey signs nothing
   * there. Nothing on chain reports that, so the screen does.
   */
  function belongsHere(account: StoredAccount): boolean {
    return account.origin === null || account.origin === window.location.origin;
  }

  /**
   * What a sign in gives back, and what it does not.
   *
   * A sign in returns the account and the funds. It returns no voucher,
   * because a voucher exists only on the device that made it.
   *
   * That costs the person no money. A voucher reserves nothing, and the chain
   * never hears of it until somebody redeems it. A voucher that nobody holds
   * is never redeemed, and the funds stay where they are.
   *
   * One case needs care, and it is the reason this text exists. A person who
   * kept a copy of a code can still redeem it. The payer voids a voucher by
   * redeeming it to their own address, and that needs the entry that the
   * device lost. So the wipe removes the remedy rather than the money, and it
   * lasts until the voucher expires.
   */
  function recoveryNotice(): HTMLElement {
    const box = make('div', 'notice');
    box.append(
      make('p', 'notice-line', 'This device has your account and your funds again.'),
      make(
        'p',
        'notice-line',
        'It does not have the vouchers that you made before. That costs you ' +
          'nothing, because a voucher holds no funds until somebody redeems it.',
      ),
      make(
        'p',
        'notice-line',
        'One case needs care. Did you show a code to somebody who kept a copy? ' +
          'That person can still use it, and this device can no longer void it. ' +
          'It stops when the voucher expires.',
      ),
    );
    return box;
  }

  /** Draw the account, and read what it holds when the network answers. */
  async function show(account: StoredAccount, recovered = false): Promise<void> {
    form.hidden = true;
    signInBox.hidden = true;
    const wrongName = !belongsHere(account);
    const box = make('div', `result result-${wrongName ? 'wait' : 'good'}`);
    box.append(
      make('h2', 'result-title', wrongName ? 'Another name holds this account' : 'Your account'),
      make(
        'p',
        'result-advice',
        wrongName
          ? `${account.origin ?? ''} created this account. A passkey works only ` +
            `under the name that created it, so this page cannot sign for it. Open ` +
            `that name, or create a new account here.`
          : 'The passkey stays in this device. There is no seed phrase.',
      ),
      detail('Address', short(account.contractId), account.contractId),
    );
    if (account.deployHash !== null) {
      box.append(detail('Created by', short(account.deployHash), account.deployHash));
    }
    const balance = make('div', 'detail');
    balance.append(make('span', 'detail-label', 'Balance'), make('span', 'detail-value', 'reading'));
    box.append(balance);

    const fund = make('button', 'submit submit-quiet', 'Add test funds');
    if (!wrongName && fund instanceof HTMLButtonElement) {
      fund.type = 'button';
      fund.addEventListener('click', () => {
        void addFunds(account, fund, balance);
      });
      box.append(fund);
    }

    const forget = make('button', 'link-button', 'Forget this device');
    if (forget instanceof HTMLButtonElement) {
      forget.type = 'button';
      forget.addEventListener('click', () => {
        clearAccount();
        showDeviceFact();
        form.hidden = false;
        signInBox.hidden = false;
        fill(panel);
      });
      box.append(forget);
    }

    if (recovered) box.append(recoveryNotice());

    fill(panel, box);
    await refreshBalance(account, balance);
  }

  /** Write one balance into the row that shows it. */
  function writeBalance(row: HTMLElement, stroops: string | null): void {
    const value = row.querySelector('.detail-value');
    if (value === null) return;
    value.textContent = stroops === null ? 'nothing yet' : xlm(stroops);
  }

  /** Read the balance and write it into the row that shows it. */
  async function refreshBalance(account: StoredAccount, row: HTMLElement): Promise<void> {
    try {
      writeBalance(row, (await fetchBalance(account.contractId)).balanceStroops);
    } catch {
      const value = row.querySelector('.detail-value');
      if (value !== null) value.textContent = 'unknown, the service did not answer';
    }
  }

  /**
   * Ask the network for test funds.
   *
   * The row says what happens while the funds reach a ledger. The old number
   * stays true for a few seconds and then stops being true. The service waits
   * for the funds and reports what the address holds, so the screen writes that
   * answer instead of a second read.
   */
  async function addFunds(
    account: StoredAccount,
    button: HTMLButtonElement,
    row: HTMLElement,
  ): Promise<void> {
    button.disabled = true;
    const value = row.querySelector('.detail-value');
    if (value !== null) value.textContent = 'adding funds';
    try {
      writeBalance(row, await fundAddress(account.contractId));
    } catch (caught) {
      showProblem(panel, reason(caught));
    } finally {
      button.disabled = false;
    }
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void register();
  });

  /** Register a passkey and deploy the account behind it. */
  async function register(): Promise<void> {
    const chain = getChain();
    if (chain === null) {
      showProblem(panel, 'The service did not answer, so this device cannot create an account yet.');
      return;
    }
    create.disabled = true;
    fill(panel, make('p', 'waiting', 'Your device is asking you to confirm the passkey.'));
    try {
      await show(await createAccount(chain, name.value.trim() || 'Senet account'));
      showDeviceFact();
    } catch (caught) {
      showProblem(panel, reason(caught));
    } finally {
      create.disabled = false;
    }
  }

  signIn.addEventListener('click', () => void recover());

  /**
   * Find the account that a passkey already holds.
   *
   * A device that lost its storage still has the passkey, and the account
   * still holds its funds on chain. This asks the authenticator, and the
   * network confirms the answer.
   */
  async function recover(): Promise<void> {
    const chain = getChain();
    if (chain === null) {
      showProblem(panel, 'The service did not answer, so this device cannot sign in yet.');
      return;
    }
    signIn.disabled = true;
    fill(panel, make('p', 'waiting', 'Your device is asking you to choose a passkey.'));
    try {
      await show(await signInAccount(chain), true);
      showDeviceFact();
    } catch (caught) {
      showProblem(panel, signInReason(caught));
    } finally {
      signIn.disabled = false;
    }
  }

  const chain = getChain();
  const account = chain === null ? getAccount() : restoreAccount(chain);
  if (account !== null) void show(account);
}
